import { Stack, Typography, useTheme } from "@mui/material";
import { type FC, useMemo } from "react";
import type { ClientPropertyEstimatedPriceHistoryResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { sortByDate } from "web_myplann/Shared/Utils/sortByDate";

type Props = {
	open: boolean;
	onClose: () => void;
	priceHistory: ClientPropertyEstimatedPriceHistoryResponseDTO[];
	currencySign: string;
};

export const MarketPriceHistoryDrawer: FC<Props> = ({
	open,
	onClose,
	priceHistory,
	currencySign,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const sortedPriceHistory = useMemo(
		() => sortByDate(priceHistory, (item) => item.estimatedPriceAt),
		[priceHistory],
	);

	return (
		<MyPlannDrawer
			grabber
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(Resources.PortfolioDetail.Property.MarketPriceHistory)}
		>
			<Stack
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.lg}
				gap={theme.spacingTokens.lg}
			>
				{sortedPriceHistory.map((item) => (
					<Stack key={item.id} gap={theme.spacingTokens.xs}>
						<Typography variant="bodyStrong">
							{`${t(Resources.Common.ToDate)} ${dateFormatter(item.estimatedPriceAt)}`}
						</Typography>

						<Stack direction="row" justifyContent="space-between">
							<Typography variant="body2">{t(Resources.Common.Min)}</Typography>
							<Typography variant="bodyMedium" tabular>
								{moneyFormatter(item.estimatedPriceMin, currencySign)}
							</Typography>
						</Stack>

						<Stack direction="row" justifyContent="space-between">
							<Typography variant="body2">{t(Resources.Common.Max)}</Typography>
							<Typography variant="bodyMedium" tabular>
								{moneyFormatter(item.estimatedPriceMax, currencySign)}
							</Typography>
						</Stack>
					</Stack>
				))}
			</Stack>
		</MyPlannDrawer>
	);
};
